import { z } from 'zod';

/**
 * ExecutionMessageType enum for standardized execution message categorization
 *
 * Phase 2 execution tracking messages (different from conversation MessageType)
 *
 * - INPUT: User or system input message
 * - INTERMEDIATE: Message generated during execution
 * - OUTPUT: Final result message
 * - ERROR: Error message
 * - METADATA: Meta information about execution
 * - SYSTEM: System-generated message
 */
export enum ExecutionMessageType {
  INPUT = 'INPUT',
  INTERMEDIATE = 'INTERMEDIATE',
  OUTPUT = 'OUTPUT',
  ERROR = 'ERROR',
  METADATA = 'METADATA',
  SYSTEM = 'SYSTEM',
}

/**
 * MessageRole enum for message source/target roles
 *
 * - user: User-initiated message
 * - assistant: AI assistant message
 * - system: System message
 * - tool: Tool execution message
 */
export enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
  TOOL = 'tool',
}

/**
 * ExecutionMessageStatus enum for message delivery tracking
 *
 * Phase 2 execution message delivery status (different from conversation MessageStatus)
 *
 * - pending: Message created, not yet processed
 * - delivered: Message delivered to target
 * - failed: Message delivery failed
 * - processed: Message processed and completed
 */
export enum ExecutionMessageStatus {
  PENDING = 'pending',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  PROCESSED = 'processed',
}

/**
 * Zod schema for ExecutionMessage validation (Phase 2 inter-service messaging)
 *
 * This is the standardized message format for Phase 2 execution tracking.
 * For agent conversation messages, use AgentMessage from agent.dto
 *
 * @[CRITICAL:SECURITY] businessId and userId are MANDATORY for multi-tenancy
 */
export const ExecutionMessageSchema = z.object({
  id: z.string().uuid().describe('Unique message identifier'),
  correlationId: z.string().describe('Groups related messages in a flow'),
  executionId: z.string().uuid().optional().describe('Associated execution ID'),
  parentId: z.string().uuid().optional().describe('Parent message for hierarchy'),
  type: z.nativeEnum(ExecutionMessageType).describe('Message category'),
  role: z.nativeEnum(MessageRole).describe('Message source/target role'),
  content: z.string().describe('Message content/payload'),
  businessId: z.string().uuid().describe('MANDATORY: Tenant isolation'),
  userId: z.string().uuid().describe('MANDATORY: User context'),
  status: z.nativeEnum(ExecutionMessageStatus).describe('Delivery status'),
  sequence: z.number().int().positive().describe('Message order within correlation'),
  sourceService: z.string().describe('Service that created this message'),
  targetService: z.string().optional().describe('Intended recipient service'),
  metadata: z.record(z.string(), z.unknown()).optional().describe('Additional context'),
  createdAt: z.date().describe('Message creation timestamp'),
  processedAt: z.date().optional().describe('Processing completion timestamp'),
});

/**
 * TypeScript type derived from Zod schema
 */
export type ExecutionMessage = z.infer<typeof ExecutionMessageSchema>;

/**
 * Factory function to create INPUT messages
 *
 * @[CRITICAL:SECURITY] Enforces businessId requirement
 */
export function createInputMessage(
  businessId: string,
  userId: string,
  content: string,
  executionId?: string,
  metadata?: Record<string, unknown>,
): ExecutionMessage {
  const id = generateUUID();
  const correlationId = executionId || generateUUID();

  return ExecutionMessageSchema.parse({
    id,
    correlationId,
    executionId,
    type: ExecutionMessageType.INPUT,
    role: MessageRole.USER,
    content,
    businessId,
    userId,
    status: ExecutionMessageStatus.PENDING,
    sequence: 1,
    sourceService: 'client',
    metadata,
    createdAt: new Date(),
  });
}

/**
 * Factory function to create OUTPUT messages
 *
 * @[CRITICAL:SECURITY] Enforces businessId requirement
 */
export function createOutputMessage(
  businessId: string,
  userId: string,
  correlationId: string,
  content: string,
  sourceService: string,
  sequence: number,
  metadata?: Record<string, unknown>,
): ExecutionMessage {
  const id = generateUUID();

  return ExecutionMessageSchema.parse({
    id,
    correlationId,
    type: ExecutionMessageType.OUTPUT,
    role: MessageRole.ASSISTANT,
    content,
    businessId,
    userId,
    status: ExecutionMessageStatus.PROCESSED,
    sequence,
    sourceService,
    metadata,
    createdAt: new Date(),
    processedAt: new Date(),
  });
}

/**
 * Factory function to create ERROR messages
 *
 * @[CRITICAL:SECURITY] Enforces businessId requirement
 */
export function createErrorMessage(
  businessId: string,
  userId: string,
  correlationId: string,
  error: Error | string,
  sourceService: string,
  sequence: number,
  targetService?: string,
): ExecutionMessage {
  const id = generateUUID();
  const errorMessage = error instanceof Error ? error.message : String(error);

  return ExecutionMessageSchema.parse({
    id,
    correlationId,
    type: ExecutionMessageType.ERROR,
    role: MessageRole.SYSTEM,
    content: errorMessage,
    businessId,
    userId,
    status: ExecutionMessageStatus.FAILED,
    sequence,
    sourceService,
    targetService,
    metadata: {
      errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
      stack: error instanceof Error ? error.stack : undefined,
    },
    createdAt: new Date(),
  });
}

/**
 * Factory function to create INTERMEDIATE messages
 *
 * @[CRITICAL:SECURITY] Enforces businessId requirement
 */
export function createIntermediateMessage(
  businessId: string,
  userId: string,
  correlationId: string,
  content: string,
  sourceService: string,
  sequence: number,
  targetService?: string,
  metadata?: Record<string, unknown>,
): ExecutionMessage {
  const id = generateUUID();

  return ExecutionMessageSchema.parse({
    id,
    correlationId,
    type: ExecutionMessageType.INTERMEDIATE,
    role: MessageRole.SYSTEM,
    content,
    businessId,
    userId,
    status: ExecutionMessageStatus.DELIVERED,
    sequence,
    sourceService,
    targetService,
    metadata,
    createdAt: new Date(),
    processedAt: new Date(),
  });
}

/**
 * Validate ExecutionMessage against schema
 *
 * Throws ZodError if validation fails
 */
export function validateExecutionMessage(data: unknown): ExecutionMessage {
  return ExecutionMessageSchema.parse(data);
}

/**
 * Safely validate ExecutionMessage with error handling
 *
 * Returns { success: true, data } or { success: false, error }
 */
export function validateExecutionMessageSafe(
  data: unknown,
): { success: true; data: ExecutionMessage } | { success: false; error: string } {
  const result = ExecutionMessageSchema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error.message };
}

function generateUUID(): string {
  if (!globalThis.crypto?.randomUUID) {
    throw new Error('crypto.randomUUID() is unavailable — cannot generate secure IDs');
  }
  return globalThis.crypto.randomUUID();
}
